/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type { Images } from "@packages/sdk";
import type { ComponentProps, ReactNode } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  numericPercentages,
  numericPixels,
  numericViewportWidth,
  radius,
  spacing,
} from "../../../../../global/emotion";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import { emotionDynamics } from "../../../../lib/emotionDynamics";
import type { RGBAObject } from "../../../../utils/colors/types/RGBAObject.types";
import { HallowImage } from "../HallowImage/HallowImage";

const imageStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsMedium,
  flexShrink: 0,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
});
const imageXXSmallStyle: Interpolation = {
  borderRadius: radius.s,
  width: 32,
  height: 32,
};
const imageXSmallStyle: Interpolation = {
  borderRadius: radius.s,
  width: 56,
  height: 56,
};
const imageSmallStyle: Interpolation = {
  borderRadius: radius.s,
  width: 72,
  height: 72,
  [cssMediaQueryMinWidthTablet]: {
    width: 80,
    height: 80,
  },
};
const imageMediumStyle: Interpolation = {
  borderRadius: radius.s,
};
const imageContainerMediumStyle: Interpolation = {
  aspectRatio: "13/9",
  height: "auto",
  lineHeight: spacing.none,
  maxWidth: spacing.full,
  position: "relative",
  width: 160,
  [cssMediaQueryMinWidthTablet]: { width: 210 },
};
const imageContainerLargeStyle: Interpolation = {
  aspectRatio: "13/9",
  height: "auto",
  lineHeight: spacing.none,
  maxWidth: numericPercentages[100],
  minWidth: numericPercentages[100],
  position: "relative",
  [cssMediaQueryMinWidthTablet]: {
    height: numericPixels[480],
    maxWidth: numericViewportWidth[70],
    minWidth: numericViewportWidth[50],
  },
};
const gradientLargeStyle: Interpolation = {
  height: numericPixels[480],
  marginLeft: "-1px",
  position: "absolute",
  width: 160,
  zIndex: 1,
};
const imageContainerSmallStyle: Interpolation = {
  overflow: "hidden",
  position: "relative",
  lineHeight: spacing.none,
  aspectRatio: 1,
  flexShrink: 0,
  minWidth: 0,
  minHeight: 0,
};

export type ThumbnailProps = {
  gradientColor?: RGBAObject;
  size: "xxs" | "xs" | "s" | "m" | "l";
  imageSrc: Images;
  imageOverlay?: ReactNode;
  title: string;
  alt?: string;
} & Omit<
  ComponentProps<"div" | "img">,
  "size" | "title" | "src" | "width" | "height"
>;

export const Thumbnail = ({
  size,
  imageSrc,
  imageOverlay,
  title,
  gradientColor = { r: 0, g: 0, b: 0 },
  alt,
  ...props
}: ThumbnailProps) => {
  const isDesktop = useIsDesktopViewport();
  try {
    const t = useTranslations();
    if (!alt) alt = t("general_img_alt", { "0": title });
  } catch (e) {
    // no i18n setup, ignore
  }

  if (size === "xxs") {
    return (
      <div css={[imageContainerSmallStyle, imageXXSmallStyle]}>
        <HallowImage
          alt={alt}
          size={size}
          src={imageSrc as Images}
          width={32}
          height={32}
          css={imageStyle}
          {...(props as Omit<
            ComponentProps<"img">,
            "width" | "height" | "src"
          >)}
        />
        {imageOverlay ?? null}
      </div>
    );
  } else if (size === "xs") {
    return (
      <div css={[imageContainerSmallStyle, imageXSmallStyle]}>
        <HallowImage
          src={imageSrc as Images}
          size={size}
          alt={alt}
          width={56}
          height={56}
          css={imageStyle}
          {...(props as Omit<
            ComponentProps<"img">,
            "width" | "height" | "src"
          >)}
        />
        {imageOverlay ?? null}
      </div>
    );
  } else if (size === "s") {
    return (
      <div css={[imageContainerSmallStyle, imageSmallStyle]}>
        <HallowImage
          src={imageSrc as Images}
          size={size}
          alt={alt}
          width={isDesktop ? 80 : 72}
          height={isDesktop ? 80 : 72}
          css={imageStyle}
          {...(props as Omit<
            ComponentProps<"img">,
            "width" | "height" | "src"
          >)}
        />
        {imageOverlay ?? null}
      </div>
    );
  } else if (size === "m") {
    return (
      <div css={[imageContainerMediumStyle, imageMediumStyle]} {...props}>
        <HallowImage
          src={imageSrc as Images}
          size={size}
          alt={alt}
          css={imageStyle}
          {...(props as Omit<
            ComponentProps<"img">,
            "width" | "height" | "src"
          >)}
        />
        {imageOverlay ?? null}
      </div>
    );
  } else {
    if (isDesktop) {
      return (
        <div css={imageContainerLargeStyle} {...props}>
          <div
            css={[
              gradientLargeStyle,
              emotionDynamics.backgroundGradient({
                red: gradientColor.r,
                green: gradientColor.g,
                blue: gradientColor.b,
                direction: "to left",
              }),
            ]}
          ></div>
          <HallowImage
            src={imageSrc as Images}
            size={size}
            alt={alt}
            css={imageStyle}
            {...(props as Omit<
              ComponentProps<"img">,
              "width" | "height" | "src"
            >)}
          />
          {imageOverlay ?? null}
        </div>
      );
    }
    return (
      <div css={[imageContainerLargeStyle, imageMediumStyle]} {...props}>
        <HallowImage
          src={imageSrc as Images}
          size={size}
          alt={alt}
          css={imageStyle}
          {...(props as Omit<
            ComponentProps<"img">,
            "width" | "height" | "src"
          >)}
        />
        {imageOverlay ?? null}
      </div>
    );
  }
};
